import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardText } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { SignaturePad } from "./SignaturePad";
import { useAuth } from "../context/AuthContext";
import { TASK_CODES } from "./forms/Form4CarePlan";

const CODE_NAMES = Object.fromEntries(TASK_CODES.map((t) => [t.code, t.name]));

export const PlanSignoffNotice = ({ client }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [ack, setAck] = useState(false);
  const [sig, setSig] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/plan-signoff`).then((r) => setStatus(r.data)).catch(() => setStatus({ required: false }));
  useEffect(() => {
    api.get("/settings/features").then((r) => setEnabled(!!r.data.careplan_acks)).catch(() => {});
    load(); /* eslint-disable-next-line */
  }, [client.id]);

  // staff sign before working a client; the office reviews/signs from the client page instead
  if (user?.role === "admin") return null;
  if (!enabled || !status || !status.required || status.signed_current) return null;

  const plan = status.plan || {};
  const tasks = plan.tasks || [];
  const schedule = Object.entries(plan.schedule || {}).filter(([, v]) => v && (v.start || v.end));

  const signOff = async () => {
    if (!ack) { toast.error("Please confirm you have read the care plan"); return; }
    if (!typedName.trim()) { toast.error("Type your full name to confirm"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/plan-signoff`, { signature_png: sig || "", typed_name: typedName.trim() });
      toast.success("Care plan acknowledged");
      setStatus({ ...status, signed_current: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="plan-signoff-modal">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900" data-testid="plan-signoff-title">
          <ClipboardText size={22} className="text-primary" /> Review this care plan before your first visit
        </p>
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          You must read and acknowledge this client's care plan before working with them. If the plan changes, you'll be asked to review it again.
        </p>

        {(plan.need || plan.goal) && (
          <div className="mt-4 rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="plan-signoff-narrative">
            {plan.need && <p className="text-sm leading-relaxed text-stone-700"><span className="font-semibold">Need: </span>{plan.need}</p>}
            {plan.goal && <p className="mt-1.5 text-sm leading-relaxed text-stone-700"><span className="font-semibold">Goal: </span>{plan.goal}</p>}
          </div>
        )}

        {tasks.length > 0 && (
          <div className="mt-4" data-testid="plan-signoff-tasks">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Tasks</p>
            <div className="mt-1.5 divide-y divide-stone-100 rounded-lg border border-stone-200">
              {tasks.map((t) => (
                <div key={t.code} className="px-3 py-2" data-testid={`plan-signoff-task-${t.code}`}>
                  <p className="text-sm font-medium text-stone-800">
                    {t.code} — {t.name || t.label || CODE_NAMES[t.code] || ""}
                    {(t.levels || []).length > 0 && <span className="ml-1.5 text-xs font-normal text-stone-500">({t.levels.join(", ")})</span>}
                  </p>
                  {t.frequency && <p className="text-xs text-stone-500">{t.frequency}</p>}
                  {t.instruction && <p className="mt-0.5 text-xs leading-relaxed text-stone-600">{t.instruction}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {schedule.length > 0 && (
          <div className="mt-4" data-testid="plan-signoff-schedule">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Schedule</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {schedule.map(([day, v]) => (
                <span key={day} className="rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground">
                  {day.slice(0, 3)} {v.start}–{v.end}{v.start2 && v.end2 ? `  ·  2nd ${v.start2}–${v.end2}` : ""}
                </span>
              ))}
            </div>
            {plan.schedule_instructions && <p className="mt-1.5 text-xs text-stone-500">{plan.schedule_instructions}</p>}
          </div>
        )}

        <div className="mt-5 space-y-4 border-t border-stone-200 pt-4">
          <label className="flex items-start gap-2 text-sm text-stone-700">
            <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="plan-signoff-ack" />
            <span>I have read and understand this care plan — the tasks, schedule, and instructions — and agree to follow it with this client.</span>
          </label>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Type your full name to confirm</p>
            <input value={typedName} onChange={(e) => setTypedName(e.target.value)}
              placeholder={user?.name || "First and last name"}
              className="mt-1 h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm"
              data-testid="plan-signoff-typed-name" />
          </div>
          <p className="text-center text-[10px] uppercase tracking-wide text-stone-300">— signature pad optional —</p>
          <SignaturePad onChange={setSig} />
          <div className="flex justify-end">
            <Button onClick={signOff} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="plan-signoff-submit">
              {busy ? "Signing..." : "Acknowledge Care Plan"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
